import {Injectable} from '@angular/core';
import {Observable, of, first, switchMap, timeout} from 'rxjs';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {KeyUtilsPL2 as KU} from '@common/utils/dist/utils/key-utils-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {EventAPIService, SendEventInput} from '@services/event-api.service';
import {ServiceDecorator} from './service-decorator';
import {ChangeEventInput} from '@services/events/entry-events';
import {PublicEventInput} from '@services/events/query-events';
import {UserStore} from '@stores/user.store';
import {EntryStore} from '@stores/entry.store';

@Injectable()
export class SyncEnforcerDecorator
  implements EventAPIService, ServiceDecorator<EventAPIService>
{
  private _service: EventAPIService;

  constructor(
    private userStore: UserStore,
    private entryStore: EntryStore,
  ) {}

  setService(service: EventAPIService): void {
    this._service = service;
  }

  sendEvent(input: SendEventInput): Observable<string | void> {
    if (U.isEmpty(this._service)) {
      throw new Error('[SyncEnforcerDecorator] service not initialized');
    }

    /**
     * Make sure entries have the required attributes
     */
    switch (input.eventType) {
      case APIM.EventIntent.EntryCreate:
      case APIM.EventIntent.EntryBatchUpdate:
      case APIM.EventIntent.EntryDeepDelete:
        const c: PL2.AnyPartialEntryWithId[] = [];
        (input as ChangeEventInput).c.forEach((e) => {
          if (U.isEmpty(e)) {
            console.warn('[SyncEnforcerDecorator#sendEvent] Empty entry', e);
            return;
          }

          if (U.isEmpty(e?.uAt) || U.isEmpty(e?.uId)) {
            const pK = KU.stringFromKey(e);
            const entryState = this.entryStore.state();
            c.push({
              ...e,
              uAt: e.uAt ?? U.timestamp(),
              uId:
                e.uId ??
                entryState[pK]?.e?.uId ??
                this.userStore.state()?.userId,
            });
          } else {
            c.push(e);
          }
        });
        if (U.isEmpty(c)) {
          console.warn('[SyncEnforcerDecorator#sendEvent] Empty payload');
          return of(void 0);
        }
        input.c = c;
        break;
      default:
      // skip
    }
    if ((input as PublicEventInput)?.unauth) {
      return this._service.sendEvent(input);
    }

    if (!U.isEmpty(this.userStore.state()?.mac)) {
      return this._service.sendEvent(input);
    }

    return this.userStore.state$.pipe(
      first((user) => !U.isEmpty(user?.mac)),
      timeout(15000),
      switchMap(() => this._service.sendEvent(input)),
    );
  }
}
